/**
 * Pure manifest selectors — the read-model the views render from.
 *
 * Framework-free and fully unit-tested; the views call these instead of
 * walking the manifest themselves.
 */
import type {
  AtlasManifest,
  CloudFunction,
  Domain,
  DomainLayer,
  Entity,
  EntityKind,
  EntityLifecycle,
  EventType,
  Feature,
  FlagCatalogRow,
  Hub,
  Reference,
  FirestorePath,
  StateMachine,
  Surface,
} from '@atlas-manifest/types';
import type { MatchCandidate } from '@atlas-manifest/parser/matchFeatureFlags.js';
import type { ObjectKind, RelationEdge } from './objectKinds.js';
import { resolveEntityKind } from './entityClassification.js';

/** Look up a single entity by id. */
export function entityById(manifest: AtlasManifest, id: string): Entity | undefined {
  return manifest.entities.find((e) => e.id === id);
}

/** Look up a single domain by id. */
export function domainById(manifest: AtlasManifest, id: string): Domain | undefined {
  return manifest.domains.find((d) => d.id === id);
}

/** Look up a single surface by id. */
export function surfaceById(manifest: AtlasManifest, id: string): Surface | undefined {
  return manifest.surfaces.find((s) => s.id === id);
}

/** Look up a single cloud function by name. */
export function cloudFunctionById(manifest: AtlasManifest, name: string): CloudFunction | undefined {
  return manifest.cloudFunctions.find((f) => f.name === name);
}

/** Look up a single event type by type string. */
export function eventTypeById(manifest: AtlasManifest, type: string): EventType | undefined {
  return manifest.events.find((e) => e.type === type);
}

/** Look up a single Feature by id — includes heuristic-derived features (selectAllFeatures), not just manifest.features. */
export function featureById(manifest: AtlasManifest, id: string): Feature | undefined {
  return selectAllFeatures(manifest).find((f) => f.id === id);
}

/** Look up a single flag catalog row by flagId. */
export function flagCatalogRowById(manifest: AtlasManifest, flagId: string): FlagCatalogRow | undefined {
  return manifest.flagCatalogRows.find((r) => r.flagId === flagId);
}

/** Look up a single hub by id. */
export function hubById(manifest: AtlasManifest, id: string): Hub | undefined {
  return manifest.hubs.find((h) => h.id === id);
}

/** Look up a single state machine by id. */
export function stateMachineById(manifest: AtlasManifest, id: string): StateMachine | undefined {
  return manifest.stateMachines.find((sm) => sm.id === id);
}

/** Domains sorted by entity count (largest first), then name. */
export function domainsSorted(manifest: AtlasManifest): Domain[] {
  return [...manifest.domains].sort(
    (a, b) => b.entityCount - a.entityCount || a.name.localeCompare(b.name),
  );
}

/** Entities belonging to a domain, sorted by name. */
export function entitiesInDomain(manifest: AtlasManifest, domainId: string): Entity[] {
  return manifest.entities
    .filter((e) => e.domain === domainId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** References that originate from an entity (its outgoing edges). */
export function outgoingRefs(manifest: AtlasManifest, entityId: string): Reference[] {
  return manifest.references.filter((r) => r.from === entityId);
}

/** References that point at an entity (its incoming edges / "used by"). */
export function incomingRefs(manifest: AtlasManifest, entityId: string): Reference[] {
  return manifest.references.filter((r) => r.to === entityId);
}

/** The Firestore path row owned by an entity, if it is persisted. */
export function pathForEntity(
  manifest: AtlasManifest,
  entityId: string,
): FirestorePath | undefined {
  return manifest.paths.find((p) => p.entityId === entityId);
}

export function pathByPath(manifest: AtlasManifest, path: string): FirestorePath | undefined {
  return manifest.paths.find((p) => p.path === path);
}

/** Every FirestorePath claimed by this entity — unlike pathForEntity's .find(), does not silently pick just the first. */
export function pathsForEntity(manifest: AtlasManifest, entityId: string): FirestorePath[] {
  return manifest.paths.filter((p) => p.entityId === entityId);
}

/**
 * Surfaces relevant to an entity. Phase-0 manifests do not yet link surfaces to
 * entities directly, so this returns the surfaces in the entity's domain — a
 * useful "usage" approximation until the surface↔entity link lands.
 */
export function surfacesForEntity(manifest: AtlasManifest, entityId: string): Surface[] {
  const entity = entityById(manifest, entityId);
  if (!entity) return [];
  return manifest.surfaces.filter((s) => s.domain === entity.domain);
}

/**
 * Cloud Functions that touch an entity — either reading or writing it.
 * Sorted alphabetically for deterministic UI.
 */
export function cloudFunctionsForEntity(
  manifest: AtlasManifest,
  entityId: string,
): CloudFunction[] {
  return manifest.cloudFunctions
    .filter((cf) => cf.reads.includes(entityId) || cf.writes.includes(entityId))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Events related to an entity — the union of (events the entity declares)
 * and (events whose payload carries the entity). Sorted by event type.
 */
export function eventsForEntity(manifest: AtlasManifest, entityId: string): EventType[] {
  const entity = entityById(manifest, entityId);
  const declared = new Set<string>(entity?.events ?? []);
  return manifest.events
    .filter((e) => declared.has(e.type) || e.carriedEntities.includes(entityId))
    .sort((a, b) => a.type.localeCompare(b.type));
}

export interface EntityDegree {
  inDegree: number;
  outDegree: number;
  total: number;
}

/** Reference degree of an entity in the graph (self-loops excluded). */
export function entityDegree(manifest: AtlasManifest, entityId: string): EntityDegree {
  let inDegree = 0;
  let outDegree = 0;
  for (const ref of manifest.references) {
    if (ref.from === ref.to) continue;
    if (ref.from === entityId) outDegree++;
    if (ref.to === entityId) inDegree++;
  }
  return { inDegree, outDegree, total: inDegree + outDegree };
}

/**
 * Precise surfaces that render an entity. Reads `Entity.surfaces` — the
 * explicit link populated by the manifest builder — and resolves to the
 * surface objects. Replaces the older domain-co-membership approximation.
 */
export function surfacesUsingEntity(manifest: AtlasManifest, entityId: string): Surface[] {
  const entity = entityById(manifest, entityId);
  if (!entity) return [];
  const surfaceIds = new Set(entity.surfaces);
  return manifest.surfaces.filter((s) => surfaceIds.has(s.id));
}

/**
 * Entities a single field on `entityId` references — the ripple-impact
 * surface for the "selected field" panel.
 */
export function fieldReferenceTargets(
  manifest: AtlasManifest,
  entityId: string,
  fieldName: string,
): string[] {
  return manifest.references
    .filter((r) => r.from === entityId && r.fromField === fieldName)
    .map((r) => r.to);
}

export type PiiLevel = 'none' | 'indirect' | 'direct';

export interface EntityMetadata {
  retention: string;
  indexes: string[];
  tierGate?: string;
  packGate?: string;
  pii: PiiLevel;
  isMinorVisible: boolean;
  firestorePath?: string;
}

function tagValue(tags: string[], prefix: string): string | undefined {
  const hit = tags.find((t) => t.startsWith(`${prefix}:`));
  return hit ? hit.slice(prefix.length + 1) : undefined;
}

/**
 * Metadata block for the entity sheet — retention, indexes, tier/pack gates,
 * PII level, minor visibility. Pulls from `Entity.tags` (canonical
 * `tier:…`, `pack:…`, `pii:…`) plus the owning Firestore path.
 */
export function entityMetadata(manifest: AtlasManifest, entityId: string): EntityMetadata {
  const entity = entityById(manifest, entityId);
  const path = entity ? pathForEntity(manifest, entityId) : undefined;
  const tags = entity?.tags ?? [];
  const piiTag = tagValue(tags, 'pii');
  const pii: PiiLevel = piiTag === 'indirect' || piiTag === 'direct' ? piiTag : 'none';
  return {
    retention: entity?.retention ?? path?.retention ?? 'ephemeral',
    indexes: path?.indexes ?? [],
    tierGate: tagValue(tags, 'tier'),
    packGate: tagValue(tags, 'pack'),
    pii,
    isMinorVisible: entity?.isMinorVisible ?? false,
    firestorePath: entity?.firestorePath,
  };
}

const MAX_FEATURES_PER_DOMAIN = 8;

function camelPrefix(entityId: string): string {
  const m = entityId.match(/^([A-Z]{2,}(?=[A-Z][a-z]|[^a-zA-Z]|$)|[A-Z][a-z]*)/);
  return m ? m[1] : entityId;
}

/**
 * Derive Feature clusters from entity naming patterns when manifest.features is absent.
 * Groups entities within each domain by CamelCase prefix (acronym-safe), caps at
 * MAX_FEATURES_PER_DOMAIN groups; excess groups are merged into "Other".
 */
export function deriveFeatures(manifest: AtlasManifest): Feature[] {
  const features: Feature[] = [];

  for (const domain of manifest.domains) {
    const domainEntities = manifest.entities.filter((e) => e.domain === domain.id);
    if (domainEntities.length === 0) continue;

    const groups = new Map<string, string[]>();
    for (const entity of domainEntities) {
      const prefix = camelPrefix(entity.id);
      const arr = groups.get(prefix) ?? [];
      arr.push(entity.id);
      groups.set(prefix, arr);
    }

    let sorted = [...groups.entries()].sort((a, b) => b[1].length - a[1].length);

    if (sorted.length > MAX_FEATURES_PER_DOMAIN) {
      const kept = sorted.slice(0, MAX_FEATURES_PER_DOMAIN - 1);
      const otherIds = sorted.slice(MAX_FEATURES_PER_DOMAIN - 1).flatMap(([, ids]) => ids);
      kept.push(['Other', otherIds]);
      sorted = kept;
    }

    const total = sorted.length;
    sorted.forEach(([prefix, entityIds], i) => {
      const angle = (2 * Math.PI * i) / total - Math.PI / 2;
      const wx = 0.5 + 0.22 * Math.cos(angle);
      const wy = 0.5 + 0.22 * Math.sin(angle);

      const eventTypes = new Set<string>();
      for (const eid of entityIds) {
        const entity = manifest.entities.find((e) => e.id === eid);
        if (entity) for (const ev of entity.events) eventTypes.add(ev);
      }

      features.push({
        id: `${domain.id}:${prefix.toLowerCase()}`,
        label: prefix.length > 12 ? prefix.slice(0, 11) + '…' : prefix,
        domainId: domain.id,
        entityIds,
        eventTypes: [...eventTypes],
        wx,
        wy,
        wz: 0,
        source: 'heuristic',
      });
    });
  }

  return features;
}

/**
 * Full Feature list across every domain: doc-section-derived entries
 * (manifest.features) merged per-domain with the deriveFeatures() heuristic
 * fallback for domains that have no doc-section entries at all. Never do a
 * manifest-wide "features.length ? features : derived" check — that picks
 * one source for every domain and silently drops heuristic clusters for
 * domains the parser didn't cover.
 */
export function selectAllFeatures(manifest: AtlasManifest): Feature[] {
  const docSectionFeatures = manifest.features ?? [];
  const domainIdsWithDocFeatures = new Set(docSectionFeatures.map((f) => f.domainId));
  const heuristicFeatures = deriveFeatures(manifest).filter(
    (f) => !domainIdsWithDocFeatures.has(f.domainId),
  );
  return [...docSectionFeatures, ...heuristicFeatures];
}

/**
 * Domains grouped by architectural layer, ordered L2 → L5. Domains with no
 * layer assignment fall into the `'unknown'` bucket.
 *
 * Used by the Layer Cosmos renderer and the grouped domain legend.
 */
export const LAYER_ORDER = ['L2', 'L3', 'L4', 'L5'] as const;

export function domainsByLayer(
  manifest: AtlasManifest,
): Map<DomainLayer | 'unknown', Domain[]> {
  const result = new Map<DomainLayer | 'unknown', Domain[]>(
    [...LAYER_ORDER, 'unknown' as const].map((l) => [l, []]),
  );
  for (const d of manifest.domains) {
    const key = d.layer ?? 'unknown';
    result.get(key)!.push(d);
  }
  return result;
}

/** Whole-manifest summary counts for the welcome hero. */
export interface ManifestSummary {
  entityCount: number;
  domainCount: number;
  surfaceCount: number;
  referenceCount: number;
}

export function manifestSummary(manifest: AtlasManifest): ManifestSummary {
  return {
    entityCount: manifest.stats.entityCount,
    domainCount: manifest.stats.domainCount,
    surfaceCount: manifest.stats.surfaceCount,
    referenceCount: manifest.stats.referenceCount,
  };
}

export type ConnectivityStatus = 'wired' | 'connected' | 'weak' | 'isolated';

export interface DomainStats {
  domainId: string;
  layer: DomainLayer;
  entityCount: number;
  eventCount: number;
  crossDomainRefCount: number;
  connectivity: ConnectivityStatus;
}

function toConnectivity(crossDomainRefs: number): ConnectivityStatus {
  if (crossDomainRefs === 0) return 'isolated';
  if (crossDomainRefs <= 2) return 'weak';
  if (crossDomainRefs <= 9) return 'connected';
  return 'wired';
}

export function computeDomainStats(manifest: AtlasManifest): DomainStats[] {
  return manifest.domains.map((domain) => {
    const entities = entitiesInDomain(manifest, domain.id);
    const events = manifest.events.filter((e) => e.category === domain.id);
    const crossDomainRefCount = entities.reduce((sum, entity) => {
      return sum + outgoingRefs(manifest, entity.id).filter((r) => r.crossDomain === true).length;
    }, 0);
    return {
      domainId: domain.id,
      layer: domain.layer ?? 'L5',
      entityCount: entities.length,
      eventCount: events.length,
      crossDomainRefCount,
      connectivity: toConnectivity(crossDomainRefCount),
    };
  });
}

export function domainStatsById(manifest: AtlasManifest, domainId: string): DomainStats | undefined {
  return computeDomainStats(manifest).find((s) => s.domainId === domainId);
}

// ─── Catalog selectors ────────────────────────────────────────────────────────

type DomainLayerOrUnknown = DomainLayer | 'unknown';

const LAYER_BASELINES: Record<DomainLayerOrUnknown, { entities: number; events: number; surfaces: number }> = {
  L2: { entities: 12, events: 6, surfaces: 4 },
  L3: { entities: 8, events: 4, surfaces: 2 },
  L4: { entities: 6, events: 3, surfaces: 2 },
  L5: { entities: 4, events: 2, surfaces: 1 },
  unknown: { entities: 6, events: 3, surfaces: 2 },
};

function computeHealthScore(
  layer: DomainLayerOrUnknown,
  entityCount: number,
  eventCount: number,
  surfaceCount: number,
): number {
  const baseline = LAYER_BASELINES[layer];
  const es = Math.min(1, entityCount / baseline.entities);
  const ev = Math.min(1, eventCount / baseline.events);
  const sf = Math.min(1, surfaceCount / baseline.surfaces);
  return Math.round((es * 0.4 + ev * 0.3 + sf * 0.3) * 100);
}

export interface CatalogDomain {
  id: string;
  name: string;
  color: string;
  layer: DomainLayerOrUnknown;
  entityCount: number;
  eventCount: number;
  surfaceCount: number;
  functionCount: number;
  referenceCount: number;
  description: string;
  packGate?: string;
  healthScore: number;
  healthSource: 'ai' | 'formula';
  /** Gap flags computed by detectDomainGaps — empty array means no gaps. */
  gapFlags: GapFlag[];
  businessIntent?: string;
  boundaryRationale?: string;
  ownerTeam?: string;
  designPrinciples?: string[];
  /** Fields on this domain populated by AI gap-fill. Non-empty → show AI badge in catalog. */
  aiEnrichedFields?: string[];
  /**
   * Status of this domain: 'draft' (sandbox-only, not yet in manifest.domains)
   * or 'published' (real domain in manifest). Currently always 'published' — sandbox
   * overlay support is Phase P2 work (threading optional overlay parameter through
   * the selector pipeline). When Phase P2 ships, this will be populated from sandbox
   * overlays; for now, it's a type placeholder.
   */
  status: 'draft' | 'published';
}

function buildCatalogDomain(manifest: AtlasManifest, d: Domain): CatalogDomain {
  const entities = manifest.entities.filter((e) => e.domain === d.id);
  const entityIds = new Set(entities.map((e) => e.id));
  const eventCount = manifest.events.filter((ev) =>
    entities.some((e) => e.events.includes(ev.type)),
  ).length;
  const surfaceCount = manifest.surfaces.filter((s) => s.domain === d.id).length;
  const functionCount = manifest.cloudFunctions.filter((f) => f.domain === d.id).length;
  const referenceCount = manifest.references.filter(
    (r) => entityIds.has(r.from) || entityIds.has(r.to),
  ).length;
  const layer = (d.layer ?? 'unknown') as DomainLayerOrUnknown;

  // Use persisted aiHealthScore from manifest; fall back to formula-based computation if absent.
  const healthScore = d.aiHealthScore ?? computeHealthScore(layer, entities.length, eventCount, surfaceCount);
  const healthSource = d.aiHealthScore !== undefined ? ('ai' as const) : ('formula' as const);

  return {
    id: d.id,
    name: d.name,
    color: d.color,
    layer,
    entityCount: entities.length,
    eventCount,
    surfaceCount,
    functionCount,
    referenceCount,
    description: d.description ?? '',
    packGate: d.packGate,
    healthScore,
    healthSource,
    gapFlags: detectDomainGaps(manifest, d.id),
    businessIntent: d.businessIntent,
    boundaryRationale: d.boundaryRationale,
    ownerTeam: d.ownerTeam,
    designPrinciples: d.designPrinciples,
    aiEnrichedFields: d.aiEnrichedFields,
    status: 'published' as const,
  };
}

export function selectCatalogDomains(manifest: AtlasManifest): CatalogDomain[] {
  return manifest.domains.map((d) => buildCatalogDomain(manifest, d));
}

/** Catalog view-model for a single domain, without computing the other domains. */
export function selectCatalogDomain(manifest: AtlasManifest, domainId: string): CatalogDomain | undefined {
  const d = domainById(manifest, domainId);
  return d ? buildCatalogDomain(manifest, d) : undefined;
}

// ─── Flag catalog selectors ───────────────────────────────────────────────────

/** View-model alias — gatingFeatureIds now lives directly on FlagCatalogRow, computed by enrichment stage 07 (relationship-closure). Kept as a named type so call sites (e.g. FeatureCatalogView.tsx) don't need to change their imports. */
export type FlagCatalogViewRow = FlagCatalogRow;

export interface FlagCatalogSelection {
  rows: FlagCatalogViewRow[];
  /** flagIds with no Feature referencing them via gatingFlagIds. */
  unlinkedFlagIds: string[];
  /** Feature ids with no matching flag — passthrough of manifest.stats.unlinkedFeatureIds. */
  unlinkedFeatureIds: string[];
  /** The resolved per-domain Feature list (selectAllFeatures(manifest)) — exposed so callers (e.g. FeatureCatalogView's Unlinked section, Task 10) can look up the label/domainId of an unlinked Feature id without recomputing the merge. */
  features: Feature[];
  /** Medium-confidence match candidates (score in [CANDIDATE_THRESHOLD, AUTO_LINK_THRESHOLD)) — passthrough of
   *  manifest.flagFeatureCandidates, exposed for the Unlinked queue's inline "Did you mean…" suggestions (Task 8). */
  flagFeatureCandidates: MatchCandidate[];
}

export function selectFlagCatalog(manifest: AtlasManifest): FlagCatalogSelection {
  const features = selectAllFeatures(manifest);
  const rows: FlagCatalogViewRow[] = manifest.flagCatalogRows;

  const unlinkedFlagIds = rows
    .filter((r) => !r.gatingFeatureIds || r.gatingFeatureIds.length === 0)
    .map((r) => r.flagId);

  return {
    rows,
    unlinkedFlagIds,
    unlinkedFeatureIds: manifest.stats.unlinkedFeatureIds,
    features,
    flagFeatureCandidates: manifest.flagFeatureCandidates ?? [],
  };
}

/** Highest-scoring candidate referencing the given flag or Feature id, if any. */
export function bestCandidateFor(
  candidates: readonly MatchCandidate[],
  side: 'flag' | 'feature',
  id: string,
): MatchCandidate | undefined {
  let best: MatchCandidate | undefined;
  for (const c of candidates) {
    if ((side === 'flag' ? c.flagId : c.featureId) !== id) continue;
    if (!best || c.score > best.score) best = c;
  }
  return best;
}

// ─── Artifact selectors ───────────────────────────────────────────────────────

export type ArtifactKind = 'entity' | 'event' | 'surface' | 'function' | 'path';

export interface ArtifactRow {
  kind: ArtifactKind;
  id: string;
  name: string;
  tags: string[];
  docRef?: string;
  /** Entity structural kind — set only on kind === 'entity' rows. */
  entityKind?: EntityKind;
  /** Design-authority lifecycle stage — set only on kind === 'entity' rows. */
  lifecycle?: EntityLifecycle;
  /** Owning entity id — set only on kind === 'path' rows (path.path itself isn't a valid route id). */
  entityId?: string;
}

export interface ArtifactFilters {
  kind?: ArtifactKind;
  query?: string;
}

export type GapKind = 'ISOLATED_DOMAIN' | 'ORPHAN_EVENT' | 'UNREFERENCED_ENTITY';

export interface GapFlag {
  kind: GapKind;
  /**
   * Severity level: 'warning' (active gap), 'error' (critical), or 'resolved'
   * (no longer applies, but surfaced for explanation).
   *
   * Today (2026-07-24): detectDomainGaps() only returns 'warning' and 'error'.
   * 'resolved' is a placeholder for future work (Phase P9+) to render entities
   * that WOULD have been flagged pre-Phase-A but are now excluded by the
   * kind filter (e.g., 'props' entities excluded from UNREFERENCED_ENTITY check).
   * When implemented, resolved flags will explain why an entity no longer matches
   * the gap rule and include the rule's scoping rationale.
   */
  severity: 'warning' | 'error' | 'resolved';
  message: string;
}

export function selectDomainArtifacts(
  manifest: AtlasManifest,
  domainId: string,
  filters: ArtifactFilters = {},
): ArtifactRow[] {
  const entities = manifest.entities.filter((e) => e.domain === domainId);
  const entityIds = new Set(entities.map((e) => e.id));

  const rows: ArtifactRow[] = [
    ...entities.map((e) => ({
      kind: 'entity' as ArtifactKind,
      id: e.id,
      name: e.name,
      tags: e.tags ?? [],
      docRef: e.docRef,
      entityKind: resolveEntityKind(e),
      lifecycle: e.lifecycle ?? 'committed',
    })),
    ...manifest.events
      .filter((ev) => entities.some((e) => e.events.includes(ev.type)))
      .map((ev) => ({
        kind: 'event' as ArtifactKind,
        id: ev.type,
        name: ev.type,
        tags: [],
        docRef: undefined,
      })),
    ...manifest.surfaces
      .filter((s) => s.domain === domainId)
      .map((s) => ({
        kind: 'surface' as ArtifactKind,
        id: s.id,
        name: s.id,
        tags: [],
        docRef: undefined,
      })),
    ...manifest.cloudFunctions
      .filter((f) => f.domain === domainId)
      .map((f) => ({
        kind: 'function' as ArtifactKind,
        id: f.name,
        name: f.name,
        tags: [f.kind],
        docRef: undefined,
      })),
    ...manifest.paths
      .filter((p) => entityIds.has(p.entityId))
      .map((p) => ({
        kind: 'path' as ArtifactKind,
        id: p.path,
        name: p.path,
        tags: [],
        docRef: undefined,
        entityId: p.entityId,
      })),
  ];

  let filtered = rows;
  if (filters.kind) filtered = filtered.filter((r) => r.kind === filters.kind);
  if (filters.query) {
    const q = filters.query.toLowerCase();
    filtered = filtered.filter(
      (r) => r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q),
    );
  }
  return filtered;
}

export function detectDomainGaps(manifest: AtlasManifest, domainId: string): GapFlag[] {
  const entities = manifest.entities.filter((e) => e.domain === domainId);
  if (entities.length === 0) return [];

  const entityIds = new Set(entities.map((e) => e.id));
  const gaps: GapFlag[] = [];

  const hasExternalRef = manifest.references.some(
    (r) =>
      (entityIds.has(r.from) && !entityIds.has(r.to)) ||
      (entityIds.has(r.to) && !entityIds.has(r.from)),
  );
  if (!hasExternalRef) {
    gaps.push({
      kind: 'ISOLATED_DOMAIN',
      severity: 'warning',
      message: 'No cross-domain references — may indicate missing integration.',
    });
  }

  const domainEventTypes = new Set(entities.flatMap((e) => e.events));
  const hasCarriedEvent = manifest.events.some((ev) => domainEventTypes.has(ev.type));
  if (!hasCarriedEvent) {
    gaps.push({
      kind: 'ORPHAN_EVENT',
      severity: 'warning',
      message: 'No events are carried by domain entities.',
    });
  }

  for (const entity of entities) {
    if (resolveEntityKind(entity) !== 'aggregate') continue;
    const hasRef = manifest.references.some((r) => r.from === entity.id || r.to === entity.id);
    if (!hasRef) {
      gaps.push({
        kind: 'UNREFERENCED_ENTITY',
        severity: 'warning',
        message: `Entity "${entity.id}" has no references.`,
      });
    }
  }

  return gaps;
}

// ─── Cross-kind relationship selector ─────────────────────────────────────────

/**
 * Flattens any object's forward + reverse edge fields into a common
 * RelationEdge[] shape — the one place that knows how to do this across all
 * 9 ObjectKinds. Detail views and the graph/chip components call only this
 * selector, never the per-kind typed fields directly.
 *
 * When a forward field and its reverse-computed counterpart describe the
 * same underlying relationship (e.g. Entity.surfaces and Entity.usedBySurfaces
 * both mean "rendered on"), both are pushed under the same relation label and
 * deduplicated below by (kind,id,relation) — forward/reverse pairs collapse to
 * single edges, while distinct relations on the same target (e.g. reading and
 * writing the same entity) each survive as separate edges.
 */
export function relationshipsFor(manifest: AtlasManifest, kind: ObjectKind, id: string): RelationEdge[] {
  const edges: RelationEdge[] = [];
  const push = (targetKind: ObjectKind, ids: string[] | undefined, relation: string): void => {
    for (const targetId of ids ?? []) edges.push({ kind: targetKind, id: targetId, relation });
  };

  switch (kind) {
    case 'entity': {
      const e = entityById(manifest, id);
      if (!e) break;
      push('surface', e.surfaces, 'rendered on');
      push('surface', e.usedBySurfaces, 'rendered on');
      push('event-type', e.events, 'carried in');
      push('event-type', e.usedByEvents, 'carried in');
      push('cloud-function', e.usedByFunctions, 'read/written by');
      push('statemachine', e.usedByStateMachines, 'mutated by');
      push('feature', e.usedByFeatures, 'used by feature');
      push('hub', e.usedByHubs, 'used by hub');
      if (e.firestorePath) edges.push({ kind: 'firestore-path', id: e.firestorePath, relation: 'stored at' });
      break;
    }
    case 'surface': {
      const s = surfaceById(manifest, id);
      if (!s) break;
      push('entity', s.entityIds, 'renders');
      if (s.hub) edges.push({ kind: 'hub', id: s.hub, relation: 'belongs to' });
      push('statemachine', s.usedByStateMachines, 'used by');
      break;
    }
    case 'cloud-function': {
      const fn = cloudFunctionById(manifest, id);
      if (!fn) break;
      push('entity', fn.reads, 'reads');
      push('entity', fn.writes, 'writes');
      push('event-type', fn.emitsEvents, 'emits');
      break;
    }
    case 'event-type': {
      const ev = eventTypeById(manifest, id);
      if (!ev) break;
      push('entity', ev.carriedEntities, 'carries');
      push('cloud-function', ev.emittedBy, 'emitted by');
      push('cloud-function', ev.consumerFunctions, 'consumed by');
      push('statemachine', ev.triggersStateMachines, 'triggers');
      push('feature', ev.usedByFeatures, 'used by feature');
      break;
    }
    case 'firestore-path': {
      const p = pathByPath(manifest, id);
      if (!p) break;
      edges.push({ kind: 'entity', id: p.entityId, relation: 'owned by' });
      break;
    }
    case 'feature': {
      const f = featureById(manifest, id);
      if (!f) break;
      push('entity', f.entityIds, 'uses');
      push('event-type', f.eventTypes, 'uses');
      push('flag', f.gatingFlagIds, 'gated by');
      break;
    }
    case 'flag': {
      const row = flagCatalogRowById(manifest, id);
      if (!row) break;
      push('flag', row.killswitchRef, 'gated by');
      push('feature', row.gatingFeatureIds, 'gates');
      push('flag', row.gatedByFlags, 'gates');
      break;
    }
    case 'hub': {
      const h = hubById(manifest, id);
      if (!h) break;
      push('surface', h.surfaceIds, 'contains');
      push('entity', h.entityIds, 'reads');
      break;
    }
    case 'statemachine': {
      const sm = stateMachineById(manifest, id);
      if (!sm) break;
      push('event-type', sm.triggeredByEvents, 'triggered by');
      push('entity', sm.mutatesEntities, 'mutates');
      push('surface', sm.surfaces, 'renders on');
      break;
    }
  }

  const seen = new Map<string, RelationEdge>();
  for (const edge of edges) {
    const key = `${edge.kind}:${edge.id}:${edge.relation}`;
    if (!seen.has(key)) seen.set(key, edge);
  }
  return [...seen.values()];
}
